import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Instagram, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import KineticTitle from "@/components/KineticTitle";
import { site, aspace } from "@/data/content";

export default function ASpaceArt() {
  return (
    <>
      <Seo
        title={"A-Space Art — " + site.name}
        description="A-Space Art: uno spazio per creare, esplorare e trasformare. Ricerca creativa tra arte, scrittura, mindfulness ed editoria creativa."
      />

      <section className="pt-36 lg:pt-44 pb-16" data-testid="aspace-hero">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bordeaux">
            {aspace.eyebrow}
          </p>
          <KineticTitle
            lines={["A-Space Art —", "uno spazio per creare,", "esplorare, trasformare."]}
            delay={0.25}
            className="mt-6 font-medium text-[clamp(2.25rem,5.5vw,4.75rem)] leading-[1.06] tracking-tight max-w-5xl"
          />
          <Reveal delay={0.45}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-ink2">
              {aspace.intro}
            </p>
          </Reveal>
          <Reveal delay={0.5}>
            <p className="mt-5 max-w-2xl font-serif italic text-xl sm:text-2xl leading-relaxed text-ink">
              {aspace.intro2}
            </p>
          </Reveal>
          <Reveal delay={0.55}>
            <p className="mt-6 max-w-2xl text-sm font-light leading-relaxed text-ink2 border-l-2 border-bordeaux pl-5" data-testid="aspace-cofondazione">
              {aspace.cofondazione}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3">
              {site.instagramASpace && (
                <a
                  href={site.instagramASpace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 link-underline text-sm"
                  data-testid="aspace-instagram-link"
                >
                  <Instagram size={16} strokeWidth={1.5} />
                  Instagram — @a_space_ita
                </a>
              )}
              {aspace.sito && (
                <a
                  href={aspace.sito}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 link-underline text-sm"
                  data-testid="aspace-sito-link"
                >
                  Sito ufficiale di A-Space Art
                  <ArrowUpRight size={15} strokeWidth={1.5} />
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24" data-testid="aspace-visual">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal>
            <img
              src={aspace.heroImage}
              alt={aspace.heroImageAlt}
              className="w-full aspect-[16/9] object-cover"
              data-testid="aspace-hero-image"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-dark text-paper py-24 lg:py-28" data-testid="aspace-manifesto">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta">
              Manifesto
            </p>
            <p className="mt-8 font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-snug">
              {aspace.manifesto}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="aspace-aree">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {aspace.areeTitle}
            </h2>
            <p className="mt-5 text-sm font-light leading-relaxed text-ink2 max-w-xs">
              {aspace.areeNote}
            </p>
          </Reveal>
          <div className="lg:col-span-8">
            {aspace.aree.map((area, i) => (
              <Reveal key={area.title} delay={i * 0.05}>
                <div className="flex items-baseline gap-6 py-7 border-t border-ink/15">
                  <p className="font-mono text-xs tracking-[0.3em] text-bordeaux">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <div>
                    <h3 className="font-serif text-2xl">{area.title}</h3>
                    <p className="mt-2 text-sm font-light text-ink2">{area.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="aspace-gallery">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 sm:grid-cols-3 gap-6 items-end">
          <Reveal className="sm:col-span-2">
            <img
              src={aspace.gallery[0].src}
              alt={aspace.gallery[0].alt}
              className="w-full aspect-[16/10] object-cover"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <img
              src={aspace.gallery[1].src}
              alt={aspace.gallery[1].alt}
              className="w-full aspect-[3/4] object-cover"
            />
          </Reveal>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="aspace-fondatrici">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-12">
              {aspace.fondatriciTitle}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 lg:max-w-3xl">
            {aspace.fondatrici.map((f, i) => (
              <Reveal key={f.nome} delay={i * 0.1}>
                <div className="text-center" data-testid={`aspace-fondatrice-card-${i + 1}`}>
                  <img
                    src={f.foto}
                    alt={f.fotoAlt}
                    className="w-full aspect-square object-cover"
                  />
                  <h3 className="mt-5 font-serif text-2xl">{f.nome}</h3>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted3">
                    {f.ruolo}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="aspace-progetti">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-10">{aspace.progettiTitle}</h2>
          </Reveal>
          {aspace.progetti.length === 0 ? (
            <Reveal delay={0.1}>
              <div className="border border-dashed border-ink/25 p-10 sm:p-14 text-center">
                <Sparkles size={20} strokeWidth={1.25} className="mx-auto text-terracotta" />
                <p className="mt-5 font-serif italic text-xl sm:text-2xl text-ink2 max-w-xl mx-auto leading-relaxed">
                  {aspace.progettiEmpty}
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {aspace.progetti.map((p, i) => (
                <Reveal key={p.titolo + p.luogo} delay={i * 0.08} className="h-full">
                  <div
                    className="h-full border border-ink/15 p-8 flex flex-col"
                    data-testid={`aspace-progetto-card-${i + 1}`}
                  >
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-bordeaux">
                      {p.tipo}
                    </p>
                    <h3 className="mt-4 font-serif text-2xl leading-snug">{p.titolo}</h3>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted3">
                      {p.anno} · {p.luogo}
                    </p>
                    <p className="mt-4 text-sm font-light leading-relaxed text-ink2">{p.descrizione}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="aspace-eventi">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
              {aspace.eventiTitle}
            </h2>
            <p className="mt-5 text-sm font-light leading-relaxed text-ink2 max-w-xs">
              {aspace.eventiNote}
            </p>
          </Reveal>
          <div className="lg:col-span-8">
            {aspace.eventi.map((evento, i) => (
              <Reveal key={evento.titolo} delay={i * 0.05}>
                <div
                  className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 py-10 border-t border-ink/15"
                  data-testid={`aspace-evento-card-${i + 1}`}
                >
                  <p className="lg:col-span-3 font-mono text-xs tracking-[0.25em] text-bordeaux pt-1">
                    {evento.anno}
                  </p>
                  <div className="lg:col-span-9">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-muted3">
                      {evento.tipo}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl sm:text-[1.7rem] leading-snug max-w-2xl">
                      {evento.titolo}
                    </h3>
                    {evento.luogo && (
                      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink2">
                        {evento.luogo}
                      </p>
                    )}
                    <p className="mt-4 text-sm font-light leading-relaxed text-ink2 max-w-2xl">
                      {evento.descrizione}
                    </p>
                    {evento.image && (
                      <img
                        src={evento.image}
                        alt={evento.imageAlt || evento.titolo}
                        className="mt-6 w-full max-w-2xl aspect-[16/9] object-cover"
                        data-testid={`aspace-evento-image-${i + 1}`}
                      />
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="aspace-collaborazioni">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="bg-sand p-10 sm:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div className="max-w-2xl">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bordeaux">
                  {aspace.collaborazioni.title}
                </p>
                <p className="mt-4 font-serif italic text-2xl sm:text-3xl leading-snug">
                  {aspace.collaborazioni.text}
                </p>
              </div>
              <Link
                to={aspace.collaborazioni.to}
                className="shrink-0 inline-flex items-center gap-3 bg-ink text-paper px-8 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-bordeaux"
                data-testid="aspace-collab-cta"
              >
                {aspace.collaborazioni.cta}
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
